import { Vector3 } from '@technology/web3d';
import { PotreeManagerModel } from '@technology/web3d-plugin-potreemanager';

type LineDetectionParameters = Parameters<
  PotreeManagerModel['findPlaneIntersectionRayNearPoint']
>[0];

type WalkParameters = Omit<LineDetectionParameters, 'limitingLineDirection'> & {
  limitingLineDirection?: Vector3;
};

export interface LineDetectionWalkSettings {
  readonly maxIterations: number;
  readonly spaceBetweenPoints: number;
  readonly maxIntersectionDistance: number;
  readonly pickVolumeSize: number;
  readonly maxAngle: number;
}

/** Demo default: ~11.25° max turn between successive edge segments. */
const DEFAULT_MAX_ANGLE_DIVISOR = 16;
const MIN_POLYLINE_POINT_COUNT = 2;

export const DEFAULT_LINE_DETECTION_WALK_SETTINGS: LineDetectionWalkSettings = {
  maxIterations: 30,
  spaceBetweenPoints: 0.4,
  maxIntersectionDistance: 0.02,
  pickVolumeSize: 0.4,
  maxAngle: Math.PI / DEFAULT_MAX_ANGLE_DIVISOR,
};

type IntersectionRay = NonNullable<
  ReturnType<PotreeManagerModel['findPlaneIntersectionRayNearPoint']>
>;

function findWalkRay(
  model: PotreeManagerModel,
  parameters: WalkParameters
): IntersectionRay | undefined {
  // Published API: required limitingLineDirection + Ray.
  // Runtime: direction optional; picker returns Ray | undefined.
  return model.findPlaneIntersectionRayNearPoint(
    parameters as LineDetectionParameters
  ) as IntersectionRay | undefined;
}

/**
 * Walks an edge from a pick point using plane-intersection rays on a potree model.
 * Mirrors the Web3D feature-extraction demo walk loop.
 */
export function detectLineAlongEdge(
  models: readonly PotreeManagerModel[],
  pickPoint: Vector3,
  settings: LineDetectionWalkSettings = DEFAULT_LINE_DETECTION_WALK_SETTINGS
): Vector3[] | undefined {
  if (models.length === 0) {
    return undefined;
  }

  const parameters: WalkParameters = {
    nearPoint: pickPoint,
    maxIntersectionDistance: settings.maxIntersectionDistance,
    pickVolumeSize: settings.pickVolumeSize,
    maxAngle: settings.maxAngle,
  };

  let model: PotreeManagerModel | undefined;
  let ray: IntersectionRay | undefined;
  for (const candidate of models) {
    const candidateRay = findWalkRay(candidate, parameters);
    if (candidateRay) {
      model = candidate;
      ray = candidateRay;
      break;
    }
  }

  if (!model || !ray) {
    return undefined;
  }

  const { spaceBetweenPoints, maxIterations } = settings;

  const forwardLine: Vector3[] = [];
  let previousRay = ray.clone();
  for (let i = 0; i < maxIterations; i++) {
    const nextPoint = previousRay.origin
      .clone()
      .add(previousRay.direction.clone().multiplyScalar(spaceBetweenPoints));
    parameters.nearPoint = nextPoint;
    parameters.limitingLineDirection = previousRay.direction.clone();
    const nextRay = findWalkRay(model, parameters);
    if (!nextRay) {
      break;
    }
    forwardLine.push(nextRay.origin.clone());
    previousRay = nextRay;
  }

  const backwardRay = ray.clone();
  backwardRay.direction.negate();

  const backwardLine: Vector3[] = [];
  previousRay = backwardRay.clone();
  for (let i = 0; i < maxIterations; i++) {
    const nextPoint = previousRay.origin
      .clone()
      .add(previousRay.direction.clone().multiplyScalar(spaceBetweenPoints));
    parameters.nearPoint = nextPoint;
    parameters.limitingLineDirection = previousRay.direction.clone();
    const nextRay = findWalkRay(model, parameters);
    if (!nextRay) {
      break;
    }
    backwardLine.push(nextRay.origin.clone());
    previousRay = nextRay;
  }

  const linePoints: Vector3[] = [];
  for (let i = backwardLine.length - 1; i >= 0; i--) {
    linePoints.push(backwardLine[i]);
  }
  // Walks start from the snapped intersection, which can differ from the raw pick.
  linePoints.push(ray.origin.clone());
  for (const point of forwardLine) {
    linePoints.push(point);
  }

  if (linePoints.length < MIN_POLYLINE_POINT_COUNT) {
    return undefined;
  }

  return linePoints;
}
